import React, { useRef, useState } from 'react';
import { ChevronsLeftRight } from 'lucide-react';
import { galleryImages } from '../mockData';

// Simulates the dull, dusty state of unpolished stone on the same photo
const DULL_FILTER = 'saturate(0.35) brightness(0.72) contrast(0.9) sepia(0.15)';

const BeforeAfterSlider = ({ before, after, dullBefore = false, testId = 'hero-before-after-slider' }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const pair = galleryImages[0];

  const afterSrc = after || pair.after;
  const beforeSrc = dullBefore ? afterSrc : before || pair.before;

  const update = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(4, p - 4));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(96, p + 4));
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      role="slider"
      aria-label="Before and after polish comparison"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      className="relative w-full h-full select-none cursor-ew-resize touch-none outline-none"
    >
      {/* After (base layer) */}
      <img src={afterSrc} alt="After diamond polishing" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      {/* Before (clipped layer) */}
      <img
        src={beforeSrc}
        alt="Before polishing"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          clipPath: `inset(0 ${100 - pos}% 0 0)`,
          ...(dullBefore ? { filter: DULL_FILTER } : {})
        }}
        draggable="false"
      />

      {/* Labels */}
      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-ink/70 text-cream px-3 py-1.5 backdrop-blur-sm">Before</span>
      <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-gold text-pine px-3 py-1.5 font-semibold">After</span>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 w-px bg-gold" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-gold text-pine flex items-center justify-center shadow-xl shadow-ink/40">
          <ChevronsLeftRight className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
